import type { VerifyAccountState } from '@/scopes/auth/domain/verifyAccountTypes'
import { IndeterminateProgressCircle } from '@sunwebgroup/uds-ui/IndeterminateProgressCircle'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import * as React from 'react'
import { useIntl } from '@/intl'
import { createVerifyAccountActions } from '@/scopes/auth/domain/verifyAccountActions'
import { useVerifyAccountStore } from '@/scopes/auth/infrastructure/useVerifyAccountStore'
import { createVerifyAccountApiClient } from '@/scopes/auth/infrastructure/verifyAccountApiClient'
import { useVerifyAccountDependencies, VerifyAccountDependenciesProvider } from '@/scopes/auth/infrastructure/verifyAccountDependencies'
import { createVerifyAccountStore } from '@/scopes/auth/infrastructure/verifyAccountStore'
import { AuthConfirmationLayout } from '@/scopes/auth/pages/components/AuthConfirmationLayout'
import { AuthHeader } from '@/scopes/auth/pages/components/AuthHeader'
import { Footer } from '@/scopes/common/components'
import { MetaCanonical } from '@/scopes/common/components/MetaCanonical'
import { MetaDescription } from '@/scopes/common/components/MetaDescription'
import { MetaRobots } from '@/scopes/common/components/MetaRobots'
import { MetaTitle } from '@/scopes/common/components/MetaTitle'
import { getContextItemId } from '@/scopes/common/domain/policies/contextItemId'
import { useConfiguration, useConfigurationClient } from '@/scopes/common/infrastructure/config/ConfigProvider'
import { useLocation } from '@/scopes/common/infrastructure/dom/location'
import { createNewRelicLogger } from '@/scopes/common/infrastructure/logging/newRelicLogger'
import queryStringClient from '@/scopes/common/infrastructure/query-strings/queryStringClient'
import layoutStyles from './Auth.module.css'
import loadingStyles from './components/AuthLoading.module.css'
import styles from './VerifyAccount.module.css'

function getIsPageInitializing(state: VerifyAccountState) {
  return state.status === 'idle' || state.status === 'loading' || state.status === 'redirecting'
}

function VerifyAccountFooter() {
  const isPageInitializing = useVerifyAccountStore(getIsPageInitializing)

  return <Footer className={isPageInitializing ? layoutStyles.footerInitializing : undefined} />
}

function VerifyAccountContent() {
  const status = useVerifyAccountStore(state => state.status)
  const { actions } = useVerifyAccountDependencies()
  const configurationClient = useConfigurationClient()
  const intl = useIntl()

  React.useEffect(() => {
    void actions.pageLoaded()
  }, [actions])

  if (status === 'idle' || status === 'loading' || status === 'redirecting') {
    return (
      <div role="status" className={loadingStyles.loader}>
        <IndeterminateProgressCircle size={32} />
        <span className={loadingStyles.loadingLabel}>
          {intl.formatMessage({ id: 'auth_verify_account_loading' })}
        </span>
      </div>
    )
  }

  const content = {
    alreadyVerified: {
      title: intl.formatMessage({ id: 'auth_verify_account_already_verified_title' }),
      message: intl.formatMessage({ id: 'auth_verify_account_already_verified_message' }),
    },
    verifiedButSignInFailed: {
      title: intl.formatMessage({ id: 'auth_verify_account_sign_in_failed_title' }),
      message: intl.formatMessage({ id: 'auth_verify_account_sign_in_failed_message' }),
    },
    expiredToken: {
      title: intl.formatMessage({ id: 'auth_verify_account_expired_title' }),
      message: intl.formatMessage({ id: 'auth_verify_account_expired_message' }),
    },
    error: {
      title: intl.formatMessage({ id: 'auth_verify_account_error_title' }),
      message: intl.formatMessage({ id: 'auth_verify_account_error_message' }),
    },
  }[status]

  return (
    <div className={layoutStyles.card}>
      <AuthConfirmationLayout title={content.title} showSpamNote={false}>
        <Typography as="p" variant="body-m-regular-snug" className={styles.message}>{content.message}</Typography>
        <Typography as="a" href={configurationClient.getField('links.login')} variant="body-m-regular-snug" className={styles.signIn}>
          {intl.formatMessage({ id: 'auth_sign_in_label' })}
        </Typography>
      </AuthConfirmationLayout>
    </div>
  )
}

export function VerifyAccount() {
  const configurationClient = useConfigurationClient()
  const siteName = useConfiguration('siteName')
  const location = useLocation()
  const [store] = React.useState(createVerifyAccountStore)
  const actions = React.useMemo(() => createVerifyAccountActions({
    apiClient: createVerifyAccountApiClient({ contextItemId: getContextItemId(siteName) }),
    store,
    queryStringClient,
    configurationClient,
    location,
    logger: createNewRelicLogger({ scope: 'auth' }),
  }), [store, configurationClient, location, siteName])
  const canonicalUrl = `${configurationClient.getField('domainUrl')}${configurationClient.getField('links.verifyAccount')}-b-variant`

  return (
    <>
      <MetaTitle titleTranslationTag="auth_verify_account_meta_title" />
      <MetaDescription descriptionTranslationTag="auth_verify_account_meta_description" />
      <MetaCanonical url={canonicalUrl} />
      <MetaRobots index={false} follow={false} />
      <VerifyAccountDependenciesProvider dependencies={{ actions, store }}>
        <div className={layoutStyles.layout}>
          <AuthHeader />
          <main className={layoutStyles.page}>
            <VerifyAccountContent />
          </main>
          <VerifyAccountFooter />
        </div>
      </VerifyAccountDependenciesProvider>
    </>
  )
}
